// src/pages/UserManagement/EditUserDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  SelectChangeEvent,
  Box
} from '@mui/material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { AdminPanelSettings as OwnerIcon, EditNote as EditorIcon } from '@mui/icons-material';

interface EditUserDialogProps {
  open: boolean;
  onClose: (refreshData?: boolean) => void;
  userData: {
    userId: string;
    displayName: string;
    role: string;
  } | null;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, onClose, userData }) => {
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('editor');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Update form when userData changes
  useEffect(() => {
    if (userData) {
      setDisplayName(userData.displayName || '');
      setRole(userData.role || 'editor');
    }
  }, [userData]);
  
  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
    }
  }, [open]);
  
  // Handle role change
  const handleRoleChange = (event: SelectChangeEvent) => {
    setRole(event.target.value);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!userData) return;
    
    // Validate inputs
    if (!displayName.trim()) {
      setError('Display name cannot be empty');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Update user document in Firestore
      await updateDoc(doc(db, 'users', userData.userId), {
        displayName,
        role,
        updatedAt: new Date()
      });
      
      setSuccess(true);
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose(true); // Refresh data
      }, 1500);
      
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!userData) return null;
  
  return (
    <Dialog 
      open={open} 
      onClose={() => !loading && onClose()} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Edit User</DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            User updated successfully!
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="Display Name"
          fullWidth
          variant="outlined"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          disabled={loading || success}
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel id="edit-role-label">User Role</InputLabel>
          <Select
            labelId="edit-role-label"
            value={role}
            label="User Role"
            onChange={handleRoleChange}
            disabled={loading || success}
          >
            <MenuItem value="editor">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EditorIcon sx={{ mr: 1 }} />
                Editor
              </Box>
            </MenuItem>
            <MenuItem value="owner">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <OwnerIcon sx={{ mr: 1 }} />
                Owner
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose()} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || success}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;